// TREC compliance checklist (ticket 206). The route is admin-only: any other seat gets a
// plain explanation instead of a spinner, and a failed load shows its reason with a retry.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';

export default function HubComplianceTab() {
  const auth = useAuth();
  const isAdmin = auth?.user?.role === 'admin';
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const load = useCallback(() => api.get('/re/broker-compliance')
    .then((r) => { setData(r.data); setLoadError(''); })
    .catch((e) => setLoadError(e?.response?.status === 403 ? 'Only company admins can see the TREC checklist.' : errorMessage(e))), []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const toggle = async (key, completed) => {
    try {
      await api.patch(`/re/broker-compliance/${key}`, null, { params: { completed } });
    } catch (e) {
      toast({ title: 'Could not update the checklist', description: errorMessage(e) });
    }
    load();
  };

  if (!isAdmin) {
    return <div className="bg-white rounded-2xl p-8 border border-dashed border-slate-200 text-center text-sm text-slate-500" data-testid="compliance-admin-only">The TREC compliance checklist is only for company admins. Ask an admin on your team if you need it.</div>;
  }
  if (loadError) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm text-sm" role="alert" data-testid="compliance-load-error">
        <div className="text-red-600">Could not load the compliance checklist: {loadError}</div>
        <button onClick={load} className="mt-2 text-xs px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50" data-testid="compliance-retry">Try again</button>
      </div>
    );
  }
  if (!data) return <div className="text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <div className="flex justify-between items-center mb-3">
          <div>
            <div className="font-serif font-semibold" style={{ color: '#070B5F' }}>TREC Brokerage Compliance ({data.state})</div>
            <div className="text-xs text-slate-500">Rules from Texas Real Estate Commission — TREC 535.2(i), 535.4, 535.154, 535.148, 535.146, and 1101.559.</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-slate-500">Compliance</div>
            <div className="font-serif text-2xl font-bold" style={{ color: data.progress >= 90 ? '#6BA82E' : data.progress >= 60 ? '#f59e0b' : '#dc2626' }}>{data.progress}%</div>
            <div className="text-xs text-slate-400">{data.completed_count} of {data.total_count}</div>
          </div>
        </div>
        <div className="space-y-1">
          {data.items.map((i) => (
            <label key={i.key} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`compliance-item-${i.key}`}>
              <input type="checkbox" checked={!!i.completed} onChange={(e) => toggle(i.key, e.target.checked)} />
              <div className="flex-1">
                <div className={`text-sm font-semibold ${i.completed ? 'line-through text-slate-400' : ''}`} style={{ color: i.completed ? undefined : '#070B5F' }}>{i.label}</div>
                {i.trec_rule && <div className="text-[10px] text-slate-400 uppercase tracking-widest">TREC {i.trec_rule}</div>}
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
